/**
 * "Who you can reach" — one row per surface that addresses this workspace's
 * contacts, and the page's only cross-channel comparison.
 *
 * WHY IT COMPARES PEOPLE AND NOTHING ELSE
 * -----------------------------------------------------------------------------
 * Every other figure on this screen belongs to one surface. This card is the
 * only place two surfaces sit beside each other, which makes it the one place
 * the cross-channel denominator defect can happen — bounce and complaint
 * computed over every channel and judged against AWS's email-only line, the
 * worst thing this project has shipped.
 *
 * So it compares the one unit its surfaces share: people on this workspace's
 * lists, by person (`person_id`, since slice 58), the unit the send path
 * reaches once. No rate is drawn here and none can be: `surface.ts` defines no ratio, and
 * the wire carries two counts per row.
 *
 * WHY THE SECOND FIGURE IS THE ONE THAT MATTERS
 * -----------------------------------------------------------------------------
 * "WhatsApp reaches 4,100" decides nothing on its own — if 4,000 of them are
 * also emailable, dropping WhatsApp costs a hundred people. `exclusive` is how
 * many a surface is the **only** way to reach, and it is what answers "is this
 * channel worth keeping". It is drawn at a lower rank than `reachable` because
 * it is the rarer, sharper number, not the headline.
 *
 * NO PROSE, AND THE BAR CARRIES THE COMPARISON
 * -----------------------------------------------------------------------------
 * The page's rule: a figure, a chip, a bar, a label. The bar is each surface's
 * reach against the workspace's whole list, so two rows can be read against one
 * another at a glance without a percentage being stated — which is exactly the
 * comparison that is honest and the one that is not.
 *
 * SLICE 66: THE BLUEPRINT'S BY-SOURCE BARS, AND WHAT THEY MAY NOT SAY
 * -----------------------------------------------------------------------------
 * The rows are `BySourceBars` now — dot, name, a meter against the whole list,
 * the count, "of N" — which is the Blueprint's own reading of exactly this
 * question ("how many of my people does each source reach"). The component's
 * header states the rule this card was built on: **a row is a count, never a
 * rate pooled across channels.** It prints `of 5,000`, never a percentage,
 * and the probe that refuses `%` anywhere on this card still stands.
 *
 * The bars are the brand sweep rather than the neutral grey they were. A
 * verdict needs a line to be judged against and reach has none: more is not
 * better if the people are wrong, and this card takes no view on that. Brand
 * is not a verdict — it is decoration — so it carries the comparison without
 * claiming one. **No status colour may reach these bars**, and the suite
 * holds that.
 *
 * `exclusive` could not ride in `BySourceBars`, whose row is one count and its
 * denominator. It is a second, smaller line under the bars, one entry per
 * surface, each still named — "Email 900 only here" — so the sharper number
 * keeps its lower rank and its own label. The two lists are drawn from the same
 * `rows` array in the same order, so they cannot disagree about which surfaces
 * exist.
 *
 * WHAT DOES NOT DRAW
 * -----------------------------------------------------------------------------
 * Surfaces outside `SURFACE_BUILT` have no row, because the API sends none — a
 * greyed line for one would be a promise on a dashboard. Facebook ads and
 * Instagram ads are built since slice 44, and WhatsApp Status ads since slice
 * 46, and still have no row, because their
 * reach is Meta's audience, counted by Meta, not people on these lists: the
 * API's ledger gates on `kind === 'direct'`. Their people are on Analytics,
 * beside their spend, in Meta's own count.
 */

import { BySourceBars, Card, CardContent, CardHeader, CardTitle } from '@wend/ui';
import type { ReachLedger } from '@wend/shared';

import { ErrorState } from '../../components/ErrorState';
import { formatCount } from '../../lib/format';
import { surfaceLabel } from '../../lib/surface-display';
import type { AsyncData } from '../../lib/use-async-data';

export function ReachLedgerCard({ reach }: { reach: AsyncData<ReachLedger> }) {
  const { data, error, status, reload } = reach;

  /* First load draws nothing: this is the heaviest read on the page and it
     must not reserve a titled box before it knows it has rows. */
  const failedOutright = status === 'error' && data === null;

  /* The workspace total is CardTitle's `sub`, as it was, but set back in sans
     tabular digits: `sub` is a mono uppercase label, and a count in mono caps
     is the dotted-zero misread the Blueprint's R6 took off every figure (slice
     66 review). The span overrides the face, the case and the tracking; same
     words, same place. Built here rather than inline so the title stays one
     `<CardTitle …>Who you can reach</CardTitle>` that verify-dashboard's
     title probe can read. */
  const peopleSub =
    data === null ? undefined : (
      <span className="font-sans text-[0.8rem] normal-case tracking-normal tabular-nums">
        {formatCount(data.people)} people
      </span>
    );
  if (data === null && !failedOutright) return null;

  return (
    <Card data-role="reach-card">
      <CardHeader className="p-[18px] pb-3.5">
        <CardTitle className="text-[0.95rem]" sub={peopleSub}>
          Who you can reach
        </CardTitle>
      </CardHeader>

      <CardContent className="p-[18px] pt-0">
        {failedOutright && (
          <ErrorState
            error={error}
            title="Could not load your reach"
            onRetry={reload}
            className="flex flex-col items-start gap-3 rounded-base border border-line px-4 py-5"
          />
        )}

        {status === 'error' && data !== null && (
          <ErrorState
            error={error}
            title="Could not refresh"
            onRetry={reload}
            className="mb-3 flex flex-col items-start gap-2 rounded-base border border-line px-4 py-3"
          />
        )}

        {data !== null && (
          <div className="flex flex-col gap-3">
            {/* Against the whole list, so two rows compare at a glance
                without a percentage being stated anywhere. The marker on each
                label is what the suite counts rows by. */}
            <BySourceBars
              data-role="reach-rows"
              rows={data.rows.map((row) => ({
                key: row.surface,
                label: (
                  <span data-role="reach-row" data-surface={row.surface}>
                    {surfaceLabel(row.surface)}
                  </span>
                ),
                count: row.reachable,
                of: data.people,
              }))}
            />
            <ul data-role="reach-exclusive" className="flex flex-wrap gap-x-5 gap-y-1 border-t border-line pt-2.5">
              {data.rows.map((row) => (
                <li key={row.surface} className="text-[0.76rem] text-muted">
                  {surfaceLabel(row.surface)}{' '}
                  <span className="font-medium tabular-nums text-text">{formatCount(row.exclusive)}</span>{' '}
                  only here
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
